/**
 * One multi-shop checkout: the separate shop orders it produced, each with its own status and its own tracking
 * page, and the combined bill. Read-only — every action (track, cancel, rate) lives on the order's own page.
 * Reached right after placing an order from several shops, and from any of those orders.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, ChevronRight, Layers, RefreshCcw, Store } from "lucide-react";
import { api, inr2 } from "../../lib/api";
import { FullPageLoader } from "../../components/Loaders";
import { ORDER_STATUS_LABEL, OVERALL_LABEL } from "../../lib/multiShop";

const pill = (status) => (status === "DELIVERED" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300"
    : status === "CANCELLED" || status === "REJECTED" ? "bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-300"
        : "bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300");

export default function OrderGroupPage() {
    const { id } = useParams();
    const { state } = useLocation();
    const [group, setGroup] = useState(null);
    const [error, setError] = useState(null);

    const load = useCallback(() => api.get(`/order-groups/${id}`).then(({ data }) => { setGroup(data); setError(null); })
        .catch((e) => setError(e?.response?.status === 404 ? "We couldn't find this checkout." : "Couldn't load your orders. Check your connection and try again.")), [id]);

    useEffect(() => {
        load();
        const timer = setInterval(load, 20000);      // each shop moves its own order along
        return () => clearInterval(timer);
    }, [load]);

    if (error && !group) {
        return (
            <div className="p-6 text-center" data-testid="order-group-error">
                <p className="text-sm text-muted-foreground">{error}</p>
                <div className="mt-4 flex justify-center gap-2">
                    <button type="button" onClick={load} className="h-10 px-4 rounded-xl border border-border text-sm font-semibold inline-flex items-center gap-2"><RefreshCcw size={15} /> Try again</button>
                    <Link to="/orders" className="h-10 px-4 rounded-xl bg-primary text-primary-foreground text-sm font-semibold inline-flex items-center">My orders</Link>
                </div>
            </div>
        );
    }
    if (!group) return <FullPageLoader />;

    const orders = group.orders || [];
    const a = group.amounts || {};
    const online = group.payment_method === "RAZORPAY";
    return (
        <div className="pb-24 lg:pb-12" data-testid="order-group-page">
            <header className="px-4 pt-5 pb-4 flex items-center gap-3 bg-card sticky top-0 z-10 border-b border-border">
                <Link to="/orders" className="p-1 -ml-1" aria-label="Back to my orders"><ArrowLeft size={22} /></Link>
                <div className="min-w-0">
                    <h1 className="text-base font-bold truncate">{orders.length ? `${orders.length} orders, one checkout` : "Your checkout"}</h1>
                    <p className="text-xs text-muted-foreground truncate">{group.reference}{group.created_at ? ` · ${new Date(group.created_at).toLocaleString()}` : ""}</p>
                </div>
            </header>

            <div className="p-4 space-y-3 lg:grid lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] lg:gap-4 lg:space-y-0 lg:items-start">
                <div className="space-y-3 min-w-0">
                    {state?.justPlaced && orders.length > 0 && (
                        <div className="rounded-2xl bg-emerald-50 border border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-900 p-4 flex items-start gap-3" data-testid="order-group-placed">
                            <CheckCircle2 size={22} className="text-emerald-600 shrink-0" />
                            <div>
                                <p className="font-bold text-sm text-emerald-800 dark:text-emerald-200">Orders placed!</p>
                                <p className="text-xs text-emerald-800/80 dark:text-emerald-200/80 mt-0.5">{orders.length} shops have your order. Each one is prepared and delivered separately — track them below.</p>
                            </div>
                        </div>
                    )}
                    {group.refund && (
                        <div className="rounded-2xl bg-amber-50 border border-amber-200 dark:bg-amber-950/30 dark:border-amber-900 p-4" data-testid="order-group-refund">
                            <p className="font-bold text-sm text-amber-900 dark:text-amber-200">{group.refund.status === "REFUNDED" ? "Payment refunded" : "Your payment is being refunded"}</p>
                            <p className="text-xs text-amber-900/80 dark:text-amber-200/80 mt-0.5">
                                This order could not be placed{group.failure ? ` (${group.failure})` : ""}. {inr2(group.refund.amount)} {group.refund.status === "REFUNDED" ? "was sent back" : "is on its way back"} to the original payment method.
                            </p>
                        </div>
                    )}

                    <section className="bg-card rounded-2xl border border-border" data-testid="order-group-orders">
                        <div className="px-4 pt-4 pb-2 flex items-center justify-between gap-2">
                            <h2 className="font-bold text-sm inline-flex items-center gap-2"><Layers size={16} className="text-primary" /> Your orders</h2>
                            <span className="text-[11px] font-semibold text-muted-foreground" data-testid="order-group-overall">{OVERALL_LABEL[group.overall] || group.overall}</span>
                        </div>
                        {orders.length === 0 ? (
                            <p className="px-4 pb-4 text-sm text-muted-foreground">No orders were created for this checkout.</p>
                        ) : (
                            <ul className="divide-y divide-border">
                                {orders.map((o) => (
                                    <li key={o.id}>
                                        <Link to={`/orders/${o.id}`} className="flex items-center gap-3 px-4 py-3.5 hover:bg-muted/40" data-testid={`order-group-order-${o.id}`}>
                                            <span className="w-10 h-10 shrink-0 rounded-xl bg-muted flex items-center justify-center text-muted-foreground"><Store size={18} /></span>
                                            <span className="flex-1 min-w-0">
                                                <span className="block text-sm font-bold truncate">{o.restaurant_name}</span>
                                                <span className="block text-xs text-muted-foreground truncate">{o.order_unique_id} · {o.items_count} item{o.items_count === 1 ? "" : "s"} · {inr2(o.grand_total)}{o.payment_status === "PAID" ? " · paid" : ""}</span>
                                            </span>
                                            <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full shrink-0 ${pill(o.status)}`}>{ORDER_STATUS_LABEL[o.status] || o.status}</span>
                                            <ChevronRight size={16} className="text-muted-foreground shrink-0" />
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>
                </div>

                {orders.length > 0 && (
                    <section className="bg-card rounded-2xl border border-border p-4 text-sm" data-testid="order-group-bill">
                        <h2 className="font-bold text-sm mb-2">Bill for this checkout</h2>
                        <Row label="Items" value={inr2(a.subtotal)} />
                        {Number(a.discount) > 0 && <Row label="Discounts" value={`− ${inr2(a.discount)}`} accent />}
                        <Row label="Delivery, taxes and charges" value={inr2(Math.max(0, (a.total || 0) - (a.subtotal || 0) + (a.discount || 0)))} />
                        <div className="border-t border-border my-2" />
                        <Row label="Total" value={inr2(a.total)} bold />
                        {Number(a.wallet_used) > 0 && <Row label="Paid from wallet" value={`− ${inr2(a.wallet_used)}`} />}
                        {Number(a.wallet_used) > 0 && <Row label={online ? "Paid online" : "To pay on delivery"} value={inr2(a.payable)} bold />}
                        <p className="mt-2 text-xs text-muted-foreground">
                            {online ? "Paid once for all shops." : group.payment_method === "COD" ? "Cash is collected separately for each shop's delivery." : "Paid from your wallet."}
                            {" "}Each order's own bill is on its tracking page.
                        </p>
                    </section>
                )}
            </div>
        </div>
    );
}

function Row({ label, value, bold, accent }) {
    return (
        <div className={`flex items-baseline justify-between gap-3 py-0.5 ${bold ? "font-bold" : ""} ${accent ? "text-emerald-700 dark:text-emerald-300" : ""}`}>
            <span className={bold || accent ? "" : "text-muted-foreground"}>{label}</span>
            <span className="tabular-nums">{value}</span>
        </div>
    );
}
